import { useCallback, useEffect, useRef, useState } from 'react';
import { outreachLeadsApi } from '../api/leads';

export interface CascadingFilterOptions {
  roles: string[];
  domains: string[];
  countries: string[];
  cities: string[];
  tags: string[];
  therapy_interest: string[];
  added_by: string[];
  lead_source: string[];
  lead_potential: string[];
}

const EMPTY_OPTIONS: CascadingFilterOptions = {
  roles: [],
  domains: [],
  countries: [],
  cities: [],
  tags: [],
  therapy_interest: [],
  added_by: [],
  lead_source: [],
  lead_potential: [],
};

export type LeadFilterOptionsParams = Parameters<typeof outreachLeadsApi.getFilterOptions>[0];

/** Remove selections that no longer exist in cascading dropdown options. */
export function pruneMultiSelect(
  selected: string[] | undefined,
  options: string[],
): string[] | undefined {
  if (!selected?.length) return undefined;
  if (!options.length) return selected;
  const lowerOptions = new Set(options.map((o) => o.toLowerCase()));
  const pruned = selected.filter((s) => lowerOptions.has(s.toLowerCase()));
  if (pruned.length === selected.length) return selected;
  return pruned.length ? pruned : undefined;
}

export function useCascadingFilterOptions(filterParams: LeadFilterOptionsParams) {
  const [filterOptions, setFilterOptions] = useState<CascadingFilterOptions>(EMPTY_OPTIONS);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const paramsKey = JSON.stringify(filterParams ?? {});

  const fetchFilterOptions = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const options = await outreachLeadsApi.getFilterOptions(filterParams);
      if (requestId === requestIdRef.current) {
        setFilterOptions(options);
      }
    } catch (error) {
      console.error('Failed to fetch filter options:', error);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
    // paramsKey keeps dependency stable across equivalent filter objects
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchFilterOptions();
  }, [fetchFilterOptions]);

  return { filterOptions, loading, refetch: fetchFilterOptions };
}
